<template>
  <v-container v-if="$can('browse', 'Contractor')">
    <!--<editor-fold desc="Page header">-->
    <v-row

      align="center"
      class="mb-6"
    >
      <v-col>
        <div class="headline">
          Contractors
        </div>
      </v-col>
      <v-col class="shrink">
        <v-btn
          v-if="$can('create', 'Contractor')"
          color="primary"
          class="ma-0"
          @click="openContractorsEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>
    <!--</editor-fold>-->

    <v-row>
      <!--<editor-fold desc="Data Filters">-->
      <v-col
        cols="3"
      >
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-text-field
          v-model="filtersDirty.name"
          solo
          hide-details
          label="Name"
          clearable
          class="mb-2"
        />
        <v-autocomplete
          v-model="filtersDirty.country_id"
          class="mb-2"
          solo
          hide-details
          label="Country"
          clearable
          :items="countries.data"
          :loading="countries.loading"
          item-value="id"
          item-text="name"
          autocomplete="disable"
        />
        <v-select
          v-model="filtersDirty.type"
          solo
          hide-details
          label="Type"
          clearable
          :items="contractorTypes"
          item-value="key"
          item-text="label"
        />
      </v-col>
      <!--</editor-fold>-->
      <v-col cols="13">
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="contractors.meta.from"
          :to="contractors.meta.to"
          :total="contractors.meta.total"
          :last-page="contractors.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
        />
        <!--</editor-fold>-->
        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="headers"
          :items="contractors.data"
          :loading="contractors.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="contractors.meta.total"
          must-sort
          hide-default-footer
          class="elevation-5"
        >
          <template v-slot:item="{ item }">
            <contractor-row
              :contractor="item"
              @click.native="openContractorsEditDialog(item)"
            />
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <v-row
              v-if="isFiltered"

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no results for your search..
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  class="ma-0"
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <v-row
              v-else

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no contractors at the moment.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  color="primary"
                  class="ma-0"
                  small
                  @click="openContractorsEditDialog()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
          </template>
          <table-footer
            slot="footer"
            :last-page="contractors.meta.last_page"
            :page.sync="pagination.page"
          />
        </v-data-table>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <contractor-edit-dialog
      :visible.sync="contractorEditDialog"
      :contractor="contractorDirty"
      @contractor:created="contractorCreated"
      @contractor:updated="contractorUpdated"
      @contractor:deleted="contractorDeleted"
    />
  </v-container>
</template>

<script>
  import { cloneDeep } from 'lodash'
  import paginationMixin from '@/mixins/paginationMixin'
  import filterableMixin from '@/mixins/filterableMixin'
  import Contractor from '@/models/Contractor'
  import ContractorRow from '@/components/business/contractors/ContractorRow'
  import ContractorEditDialog from '@/components/business/contractors/ContractorEditDialog'
  import TableFooter from '@/components/helpers/TableFooter'

  export default {
    name: 'ContractorsIndex',
    components: {
      ContractorRow,
      ContractorEditDialog,
      TableFooter
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        contractors: {
          data: [],
          loading: false,
          meta: {}
        },
        headers: [
          { text: 'Name', value: 'name', sortable: true },
          { text: 'Type', value: 'type_label', sortable: false },
          { text: 'Description', value: 'description', sortable: false },
          { text: 'Country', value: 'country_id', sortable: false }
        ],
        contractorDirty: null,
        filtersClean: Contractor.emptyFilters,
        filtersDirty: cloneDeep(Contractor.emptyFilters),
        contractorEditDialog: false,
        contractorTypes: Contractor.typeData
      }
    },
    computed: {
      countries() {
        return this.$store.state.settings.countries
      }
    },
    created() {
      this.fetchContractors()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchContractors()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      async fetchContractors() {
        this.contractors.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams
        }

        try {
          let result = await Contractor.api.fetch(queryParams)

          this.contractors.data = result.data
          this.contractors.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.contractors.loading = false
        }
      },
      openContractorsEditDialog(contractor = Contractor.emptyModel) {
        if (!this.$can('edit', 'Contractor')) return

        this.contractorDirty = cloneDeep(contractor)
        this.contractorEditDialog = true
      },
      contractorCreated(contractor) {
        this.contractors.data.unshift(contractor)
      },
      contractorUpdated(contractor) {
        const index = this.contractors.data.findIndex(b => b.id === contractor.id)

        this.contractors.data.splice(index, 1, contractor)
      },
      contractorDeleted(contractorId) {
        const index = this.contractors.data.findIndex(b => b.id === contractorId)

        this.contractors.data.splice(index, 1)
      }
    }
  }
</script>
